import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, from } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { StorageService } from './storage.service';
import { Brewery } from '../interfaces/brewery';
import { User } from '../interfaces/user';
import { Branch } from '../interfaces/branch';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private authStateChanged = new BehaviorSubject<boolean>(false);
  private storageReady = new BehaviorSubject<boolean>(false);
  private loginCompleted = new BehaviorSubject<boolean>(false);

  constructor(private http: HttpClient, private storageService: StorageService) {
    this.initializeStorageState();
  }

  private async initializeStorageState() {
    // Check if we already have a token in storage
    console.log('[AUTH] Initializing storage state');
    const token = await this.getToken();
    const hasToken = !!token;
    console.log('[AUTH] Initial token check:', hasToken ? 'HAS TOKEN' : 'NO TOKEN');
    // Storage is now initialized and ready to use
    this.storageReady.next(true);
    // If we have a token, consider login already completed
    if (hasToken) {
      this.loginCompleted.next(true);
    }
  }

  login(email: string, password: string): Observable<any> {
    console.log('[AUTH] Login API call - START');
    // Try branchlogin first, fallback to brewerylogin
    return this.http.post(`${environment.apiUrl}auth/branchlogin`, { email, password })
      .pipe(
        tap(() => console.log('[AUTH] Login API call - RESPONSE RECEIVED')),
        switchMap((response: any) => {
          console.log('[AUTH] Starting saveLoginData');
          return from(this.saveLoginData(response)).pipe(
            tap(() => {
              console.log('[AUTH] saveLoginData - COMPLETE');
              console.log('[AUTH] Setting loginCompleted = true');
              this.loginCompleted.next(true);
              this.authStateChanged.next(true);
            }),
            switchMap(() => [response])
          );
        })
      );
  }

  loginBrewery(email: string, password: string): Observable<any> {
    return this.http.post(`${environment.apiUrl}auth/brewerylogin`, { email, password })
      .pipe(
        switchMap((response: any) => {
          return from(this.saveLoginData(response)).pipe(
            tap(() => {
              this.loginCompleted.next(true);
              this.authStateChanged.next(true);
            }),
            switchMap(() => [response])
          );
        })
      );
  }

  private async saveLoginData(response: any): Promise<void> {
    console.log('[AUTH] saveLoginData - Preparing storage operations');
    const saveOperations = [];
    
    if (response.token) {
      saveOperations.push(this.storageService.setStorage('token', response.token));
    }
    if (response.refreshToken) {
      saveOperations.push(this.storageService.setStorage('refreshToken', response.refreshToken));
    }
    if (response.user.branch) {
      saveOperations.push(this.storageService.setStorage('branch', JSON.stringify(response.user.branch)));
    }
    if (response.user) {
      saveOperations.push(this.storageService.setStorage('user', JSON.stringify(response.user)));
    }
    // Brewery admin: save brewery with branches
    if (response.user.brewery) {
      saveOperations.push(this.storageService.setStorage('brewery', JSON.stringify(response.user.brewery)));
    }
    
    console.log(`[AUTH] Waiting for ${saveOperations.length} storage operations`);
    await Promise.all(saveOperations);
    console.log('[AUTH] All storage operations - COMPLETE');
  }

  sendForgotPasswordLink(email: string): Observable<any> {
    return this.http.post(`${environment.apiUrl}auth/forgot-password`, { email });
  }

  getAuthStateChanged(): Observable<boolean> {
    return this.authStateChanged.asObservable();
  }

  async logout(): Promise<void> {
    console.log('[AUTH] Logout - START');
    this.loginCompleted.next(false);
    const refreshToken = await this.getRefreshToken();
    if (refreshToken) {
      this.http.post(`${environment.apiUrl}auth/logout`, { refreshToken }).subscribe({ error: () => {} });
    }
    await this.clearAllStorageData();
    this.authStateChanged.next(false);
    console.log('[AUTH] Logout - COMPLETE');
  }

  private async clearAllStorageData(): Promise<void> {
    await Promise.all([
      this.storageService.removeStorage('token'),
      this.storageService.removeStorage('refreshToken'),
      this.storageService.removeStorage('brewery'),
      this.storageService.removeStorage('branch'),
      this.storageService.removeStorage('user')
    ]);
  }

  async getRefreshToken(): Promise<string | null> {
    const result = await this.storageService.getStorage('refreshToken');
    return result?.value || null;
  }

  async refreshTokens(): Promise<{ token: string; refreshToken: string }> {
    const refreshToken = await this.getRefreshToken();
    const response: any = await this.http
      .post(`${environment.apiUrl}auth/refresh`, { refreshToken })
      .toPromise();
    await Promise.all([
      this.storageService.setStorage('token', response.token),
      this.storageService.setStorage('refreshToken', response.refreshToken),
    ]);
    return response;
  }

  async getToken(): Promise<string | null> {
    const result = await this.storageService.getStorage('token');
    return result?.value || null;
  }

  async getBrewery() {
    const result = await this.storageService.getStorage('brewery');
    if (typeof result.value === 'string') {     
      return JSON.parse(result.value) as Brewery;
    }
    return null
  }

  async getBranch() {
    const result = await this.storageService.getStorage('branch');
    if (typeof result.value === 'string') {     
      return JSON.parse(result.value) as Branch;
    }
    return null
  }

  async getUser() {
    const result = await this.storageService.getStorage('user');
    if (typeof result.value === 'string') {     
      return JSON.parse(result.value) as User;
    }
    return null
  }
  
  async isAuthenticated(): Promise<boolean> {
    const token = await this.getToken();
    return !!token;
  }

  async isBreweryAdmin(): Promise<boolean> {
    const user = await this.getUser();
    return user?.roleId === 3;
  }

  async waitForStorageReady(): Promise<void> {
    console.log('[AUTH] waitForStorageReady - Checking if storage is ready');
    // If storage is already ready, return immediately
    if (this.storageReady.value) {
      console.log('[AUTH] Storage already ready - continuing');
      return;
    }

    console.log('[AUTH] Storage NOT ready - waiting...');
    // Otherwise wait for storage to be ready
    return new Promise((resolve) => {
      const subscription = this.storageReady.subscribe(ready => {
        if (ready) {
          console.log('[AUTH] Storage became ready - continuing');
          subscription.unsubscribe();
          resolve();
        }
      });
    });
  }

  async waitForLoginComplete(): Promise<void> {
    console.log('[AUTH] waitForLoginComplete - Checking if login is complete');
    // If login is already complete, return immediately
    if (this.loginCompleted.value) {
      console.log('[AUTH] Login already complete - continuing');
      return;
    }

    console.log('[AUTH] Login NOT complete - waiting...');
    // Otherwise wait for login to complete
    return new Promise((resolve) => {
      const subscription = this.loginCompleted.subscribe(complete => {
        if (complete) {
          console.log('[AUTH] Login became complete - continuing');
          subscription.unsubscribe();
          resolve();
        }
      });
    });
  }
  getLoginCompleted(): Observable<boolean> {
    return this.loginCompleted.asObservable();
  }

  resetLoginState(): void {
    console.log('[AUTH] Resetting login state (401 detected)');
    this.loginCompleted.next(false);
  }

}